import { useState } from 'react'
import { Wallet, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useRecordPayment } from '@/features/billing/use-recovery'
import {
  METHODS_REQUIRING_PROOF,
  PAYMENT_METHOD_LABELS,
  type PaymentMethod,
} from '@/features/billing/recovery.types'

interface RecordPaymentDialogProps {
  driverId: string
  driverName: string
  onClose: () => void
  onRecorded: () => void
}

const ACCEPTED_TYPES = 'image/jpeg,image/png,application/pdf'

/**
 * Dialog « Enregistrer un paiement » (SCRUM-95/96). Montant, méthode, référence et
 * justificatif (obligatoire pour un virement/D17). Le fichier est téléversé sur S3
 * via URL presignée par le hook, puis le paiement est créé.
 */
export default function RecordPaymentDialog({
  driverId,
  driverName,
  onClose,
  onRecorded,
}: RecordPaymentDialogProps) {
  const [amount, setAmount] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('CASH_PARTNER')
  const [reference, setReference] = useState('')
  const [proofFile, setProofFile] = useState<File | null>(null)

  const recordPayment = useRecordPayment(driverId)
  const proofRequired = METHODS_REQUIRING_PROOF.includes(method)
  const amountValue = Number(amount)
  const isValid =
    Number.isFinite(amountValue) && amountValue > 0 && (!proofRequired || proofFile !== null)

  const submit = () => {
    if (!isValid) return
    recordPayment.mutate(
      { driverId, amount: amountValue, method, reference, proofFile },
      { onSuccess: onRecorded },
    )
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/55"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-surface rounded-[20px] shadow-2xl w-[460px] max-w-[92vw] p-[26px]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/[0.14] flex items-center justify-center shrink-0">
            <Wallet size={22} strokeWidth={2.4} className="text-ink" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[19px] text-ink tracking-tight">
              Enregistrer un paiement
            </div>
            <div className="text-[13px] text-muted mt-px truncate">{driverName}</div>
          </div>
          <button
            onClick={onClose}
            disabled={recordPayment.isPending}
            className="text-muted hover:text-ink"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mt-[22px] flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
              Montant (DT)
            </span>
            <Input
              type="number"
              inputMode="decimal"
              min="0"
              step="0.001"
              aria-label="Montant"
              placeholder="50.300"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
              Méthode
            </span>
            <select
              aria-label="Méthode de paiement"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              className="h-10 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
              Référence {method === 'BANK_TRANSFER_D17' ? '' : '· optionnel'}
            </span>
            <Input
              aria-label="Référence"
              placeholder="D17-2026-0042"
              maxLength={120}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-muted">
              Justificatif {proofRequired ? '· obligatoire' : '· optionnel'}
            </span>
            <input
              type="file"
              aria-label="Justificatif"
              accept={ACCEPTED_TYPES}
              onChange={(e) => setProofFile(e.target.files?.[0] ?? null)}
              className="text-[13px] text-ink-soft file:mr-3 file:rounded-md file:border-0 file:bg-surfaceAlt file:px-3 file:py-1.5 file:text-[12.5px] file:font-semibold"
            />
            {proofRequired && !proofFile && (
              <span className="text-[11.5px] text-muted">
                Un justificatif (image ou PDF) est requis pour un virement / D17.
              </span>
            )}
          </label>
        </div>

        {recordPayment.isError && (
          <p className="mt-3 text-xs text-danger">
            Échec de l'enregistrement du paiement — vérifiez les champs et réessayez.
          </p>
        )}

        <div className="flex gap-3 mt-[22px]">
          <Button
            variant="outline"
            className="flex-1 h-12 rounded-button"
            onClick={onClose}
            disabled={recordPayment.isPending}
          >
            Annuler
          </Button>
          <button
            onClick={submit}
            disabled={!isValid || recordPayment.isPending}
            className="flex-[1.3] inline-flex items-center justify-center gap-2 bg-ink text-surface rounded-button h-12 text-sm font-semibold disabled:opacity-40 transition-opacity hover:opacity-90"
          >
            <Wallet size={18} strokeWidth={2.2} />
            {recordPayment.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}
